import { publicFileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageDropzone from "@/components/ImageDropzone";
import { useImageUpload } from "@/hooks/useImageUpload";
import { useDoctorForm } from "@/hooks/useDoctorForm";
import ScheduleFields from "@/components/ScheduleFields";
import { Loader2 } from "lucide-react";

const EMPTY = {
  name: "", specialty: "", fee: "", avatar: "", about: "",
  location: "", available_days: "", available_hours: "",
  work_days: [0, 1, 2, 3, 4, 5], start_time: "10:00", end_time: "17:00", slot_minutes: 30,
};
const FIELDS = [
  { key: "name", label: "Name", placeholder: "Dr. Full Name", testid: "doctor-name-input" },
  { key: "specialty", label: "Specialty", placeholder: "e.g. Cardiologist", testid: "doctor-specialty-input" },
  { key: "fee", label: "Consultation Fee (₹)", placeholder: "500", testid: "doctor-fee-input", type: "number" },
];
const SCHEDULE_FIELDS = [
  { key: "location", label: "Location / Place", placeholder: "Clinic name, area, city", testid: "doctor-location-input", wide: true },
];

const TextField = ({ field, value, onChange }) => (
  <div className={`space-y-2 ${field.wide ? "sm:col-span-2" : ""}`}>
    <Label>{field.label}</Label>
    <Input data-testid={field.testid} type={field.type || "text"} value={value} onChange={(e) => onChange(field.key, e.target.value)} placeholder={field.placeholder} />
  </div>
);

export default function DoctorFormDialog({ open, onOpenChange, onSaved, editing }) {
  const { form, set, saving, submit } = useDoctorForm({ open, editing, onOpenChange, onSaved, empty: EMPTY });
  const upload = useImageUpload({ isPublic: true, onUploaded: (data) => set("avatar", publicFileUrl(data.path)) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="doctor-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-2xl">{editing ? "Edit Doctor" : "Add Doctor"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-2">
          {FIELDS.map((f) => <TextField key={f.key} field={f} value={form[f.key]} onChange={set} />)}
          <div className="space-y-2">
            <Label>Doctor Photo</Label>
            <ImageDropzone upload={upload} testid="doctor-photo-upload" label="Upload doctor photo" compact onClear={() => set("avatar", "")}
              preview={form.avatar ? <img src={form.avatar} alt="doctor" className="h-24 w-24 object-cover rounded-xl border" /> : null} />
          </div>
          {SCHEDULE_FIELDS.map((f) => <TextField key={f.key} field={f} value={form[f.key]} onChange={set} />)}
          <ScheduleFields form={form} set={set} />
          <div className="space-y-2 sm:col-span-2">
            <Label>About</Label>
            <Textarea data-testid="doctor-about-input" rows={3} value={form.about} onChange={(e) => set("about", e.target.value)} placeholder="Short bio / expertise" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="doctor-form-submit-button" onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {!saving && (editing ? "Save Changes" : "Add Doctor")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
